import { Character, eDeathReason } from "./Character";
import { DeliveryBonus } from "../Parcel/DeliveryBonus";
import { Gameplay, TIMER, eState } from "../Gameplay";
import { GameWorld } from "../Game World/GameWorld";
import { NodeList } from "../../Utils/Collections/NodeList";
import { Node } from "../../Utils/Collections/Node";
import { DeliveryPoint } from "../Parcel/DeliveryPoint";
import { BonusManager } from "../Bonuses/BonusManager";
import { eBonusUID } from "../Bonuses/Bonus";
import { HUD } from "../HUD/HUD";
import { ParcelManager } from "../Parcel/ParcelManager";
import { Game, eAudioAsset, eSfxSprite, SfxSprites } from "../../Game";
import { PlayerSettings } from "../LevelSettings";
import { LevelManager } from "../Levels";
import { SoundManager } from "../../Utils/SoundManager";
import * as Gamee from "../../Gamee/GameeWrapper";



export const enum ePlayerState {
    idle,
    walk,
    death
}



/**
 * Umisteni postavy ve formaci hrace
 */
export const enum ePlayerFormationPos {
    top,
    between,
    bottom,
}



export class Player {
    /**
     * Udalost vyvolana po dokonceni smrti vsech postav
     * Emitter: Gameplay.events
     * Parametry: player: Player
     */
    static readonly EVENT_DEATH = "pl:death";

    /**
     * Udalost vyvolana po doruceni balicku do vydejniho mista
     * Emitter: Gameplay.events
     * Parametry: 
     * pkgCnt: number - pocet dorucenych balicku v aktualnim doruceni
     * totalPkgCnt: number - celkovy pocet dorucenych balicku
     */
    static readonly EVENT_PARCEL_DELIVERED = "pl:pkgDelivered";

    /**
     * Udalost vyvolana po zmene poctu nesenych balicku
     * Emitter: Gameplay.events
     * Parametry:
     * - parcelCnt: number (Celkovy pocet balicku, ktere hrac nese)
     * - maxParcelCntPerChar: number (Nejvyssi pocet balicku, ktere nese postava hrace)
     */
    static readonly EVENT_PARCEL_CNT_CHANGE = "pl:pkgPickedUp";

    /**
     * Udalost vyvolana po zmene stavu hrace
     * Emitter: Gameplay.events
     * Parametry:
     * - state: ePlayerState - novy stav hrace
     */
    static readonly EVENT_STATE_CHANGE = "pl:stateChange";

    /**
     * Snizeni rychlosti hrace za kazdy neseny balicek v jednotkach ze vterinu
     */
    static readonly WALK_SPEED_PKG_SLOWDOWN = 0.18;

    /**
     * Max pocet balicku, ktere hrac muze nest
     */
    static readonly PKG_MAX_CNT = 3;

    /**
     * Maximalni pocet postavicek, ktere hrac ovlada
     */
    static readonly CHAR_MAX_CNT = 3;

    /**
     * (RO) Staticky pristup
     */
    static get instance(): Player { return Player._instance; }
    private static _instance: Player;

    /**
     * Postavy, ktere hrac ovlada.
     */
    private _characters: Character[];

    /**
     * Seznam zivych postav serazenych podle pozice ve formaci na ose Z (od nejmensi pozice po nejvyssi)
     */
    private _liveChars: NodeList<Character>;

    /**
     * Seznam umirajicich postav
     */
    private _dyingChars: NodeList<Character>;

    /**
     * (RO) 
     */
    get liveCharCnt(): number { return this._liveChars.size; }

    /**
     * (RO) Aktualni stav hrace
     */
    get state(): ePlayerState { return this._state; }
    private _state: ePlayerState;

    /**
     * Aktualni smer pohybu hrace (1 = do plusu na Z, -1 = do minusu na Z)
     */
    get dir(): number { return this._dir; }
    private _dir: number;

    /**
     * (RO) Origin pozice kamery na ose Z
     */
    get cameraOrigin(): number { return this._camOrigin; }
    private _camOrigin: number;

    /**
     * (RO) Cas, kdy doslo k zahajeni chuze
     */
    get walkStartTime(): number { return this._walkStartTime; }
    private _walkStartTime: number;

    /**
     * (RO) Aktualni rychlost chuze hrace v jednotkach za vterinu. Meni se podle pocu balicku, ktere postavicky nesou (vzdy podle postavy, ktera nese nejvice balicku)
     */
    get walkSpeed(): number { return this._defWalkSpeed - (this._pkgCharMaxCnt * Player.WALK_SPEED_PKG_SLOWDOWN); }

    /**
     * (RO) Zakladni rychlost chuze hrace v jednotkach ze vterinu.
     */
    get defWalkSpeed(): number { return this._defWalkSpeed; }
    private _defWalkSpeed: number;

    /**
     * (RO) Bonus za vcasne doruceni balicku
     */
    get deliveryBonus(): DeliveryBonus { return this._deliveryBonus; }
    private _deliveryBonus: DeliveryBonus;

    /**
     * Minimalni pozice hrace na ose Z pro aktualni svet
     */
    private _minZ: number;

    /**
     * Maximalni pozice hrace na ose Z pro aktualni svet
     */
    private _maxZ: number;

    /**
     * (RO) Celkovy pocet balicku, ktere nesou postavy hrace
     */
    get parcelCnt(): number { return this._pkgCnt; }
    private _pkgCnt: number;

    /**
     * (RO) Celkovy pocet dorucenych balicku od resetu hrace
     */
    get deliveredPkgCnt(): number { return this._deliveredPkgCnt; }
    private _deliveredPkgCnt: number;

    /**
     * Nejvyssi pocet balicku nesenych jednou postavou
     */
    private _pkgCharMaxCnt: number;

    /**
     * Cas, kdy doslo k poslednimu prehrani zvuku sebrani balicku
     */
    private _pkgPickupSfxTime: number;

    /**
     * (RO) soundComponenta pro prehravani zvuku
     */
    get soundComponent(): pc.SoundComponent { return this._audioEntity.sound; }
    private _audioEntity: pc.Entity;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        Player._instance = this;

        let gameplay = Gameplay.instance;

        // Vytvorit jednotlive postavy
        this._characters = [];
        this._liveChars = new NodeList<Character>();
        this._dyingChars = new NodeList<Character>();

        // Zachytavat udalosti postav
        gameplay.events.on(Character.EVENT_DEATH, this.handleCharDeath, this);
        gameplay.events.on(Character.EVENT_PACKAGE_PICKUP, this.handleCharPkgPickup, this);

        // Bonus za doruceni balicku
        this._deliveryBonus = new DeliveryBonus();

        // Audio
        this._audioEntity = new pc.Entity();
        this._audioEntity.addComponent(pc.eCompomentType.audiolistener);
        Game.APP.root.addChild(this._audioEntity);
        SoundManager.createSoundComponent(this._audioEntity);

        this._audioEntity.sound.addSlot(eAudioAsset.music, {
            loop: true,
            asset: Game.APP.assets.find(eAudioAsset.music, pc.eAssetType.audio).id
        });

        let sfxAssetId = Game.APP.assets.find(eAudioAsset.sfx, pc.eAssetType.audio).id;

        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.bonusPickup, SfxSprites[eSfxSprite.bonusPickup], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.plDeath, SfxSprites[eSfxSprite.plDeath], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.plChangeDir_0, SfxSprites[eSfxSprite.plChangeDir_0], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.plChangeDir_1, SfxSprites[eSfxSprite.plChangeDir_1], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.carHit, SfxSprites[eSfxSprite.carHit], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.pkgDelivery, SfxSprites[eSfxSprite.pkgDelivery], sfxAssetId);
        SoundManager.addSoundSlot(this._audioEntity, eSfxSprite.pkgPickup, SfxSprites[eSfxSprite.pkgPickup], sfxAssetId);

        // GAMEE
        Gamee.Wrapper.EVENTS.on(Gamee.eEvent.unmute, () => { SoundManager.mute = false; SoundManager.playSound(this._audioEntity, eAudioAsset.music); }, this);
        Gamee.Wrapper.EVENTS.on(Gamee.eEvent.start, () => { SoundManager.playSound(this._audioEntity, eAudioAsset.music); }, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(settings: PlayerSettings): void {
        // Zajistit dostatecny pocet objektu pro postavicky
        while (this._characters.length < settings.charCnt)
            this._characters.push(new Character(this));

        // Aktivovat pozadovany pocet postav a ostatni deaktivovat
        this._characters.forEach((char: Character, id: number) => {
            if (id < settings.charCnt) {
                char.activate(id);
            }
            else {
                char.deactivate();
            }
        });

        // Zakladni rychlost chuze
        this._defWalkSpeed = settings.walkSpeed;

        // Vypocitat min a max pozici hrace na ose Z
        this._minZ = settings.topPadding;
        this._maxZ = GameWorld.instance.size - settings.botPadding;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(revive: boolean): void {
        let settings = LevelManager.INSTANCE.lvlSettings.player;

        // Stav
        this._state = ePlayerState.idle;

        // Startovni pozice a smer
        let startDir = settings.startDir;
        let startPos = startDir > 0 ? settings.startPadding : GameWorld.instance.size - settings.startPadding;

        this._dir = startDir;

        // Resetovat postavy
        let formPos = settings.charsOffset[0].y;
        let formOffset = 0;

        this._liveChars.clear();
        this._dyingChars.clear();

        this._characters.every((char: Character, i: number): boolean => {
            if (char.id < 0)
                return false;

            let offset = settings.charsOffset[i];

            char.reset(GameWorld.PATH_X + GameWorld.PATH_WIDTH / 2 + offset.x, startPos + offset.y, startDir);
            this._liveChars.add(char);

            // Nastavit top offset
            if (offset.y != formPos) {
                formPos = offset.y;
                formOffset++;
            }

            char.formationTopOffset = formOffset;

            return true;
        });

        // Nastavit bottom offset
        this._characters.every((char: Character): boolean => {
            if (char.id < 0)
                return false;

            char.formationBotOffset = formOffset - char.formationTopOffset;
            return true;
        });

        // Balicky
        this._pkgCnt = 0;
        this._pkgCharMaxCnt = 0;
        this._pkgPickupSfxTime = 0;

        if (!revive)
            this._deliveredPkgCnt = 0;

        // Pozice kamery
        this.updateCameraOrigin();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        // Postavy
        this._liveChars.forEach((char: Character) => { char.update(); });
        this._dyingChars.forEach((char: Character, node: Node<Character>) => {
            if (!char.update()) {
                // Odstranit postavu ze seznamu aktivnich
                this._dyingChars.removeByNode(node);

                if (this._dyingChars.empty && this._liveChars.empty) {
                    // Vsechny postavy jsou po smrti
                    this._state = ePlayerState.death;

                    Gameplay.instance.events.fire(Player.EVENT_STATE_CHANGE, this._state);
                    Gameplay.instance.events.fire(Player.EVENT_DEATH, this);
                }
            }
        });

        if (this._state != ePlayerState.walk || this._liveChars.empty)
            return;

        // Kontrolovat dosazeni min/max pozice na Z        
        let posShift: number;

        if (this._dir > 0) {
            if (this._liveChars.last.position.z >= this._maxZ) {
                posShift = this._maxZ - this._liveChars.last.position.z;
            }
        }
        else if (this._liveChars.first.position.z <= this._minZ) {
            posShift = this._minZ - this._liveChars.first.position.z;
        }

        if (posShift != undefined) {
            // Odevzdat balicky
            if (this._pkgCnt != 0) {
                ParcelManager.instance.deliveryPoints.every((point: DeliveryPoint): boolean => {
                    if (this._dir == point.dir) {
                        let pkgCnt = this._pkgCnt;

                        this._deliveredPkgCnt += pkgCnt;

                        point.showDeliveryFx();
                        this._liveChars.forEach((char: Character) => { char.deliverPackages(point); });

                        // Navysit skore
                        let scorePerPkg = this._deliveryBonus.bonus;
                        let scoreMultiplier = pkgCnt;

                        if (BonusManager.instance.isBonusActive(eBonusUID.scoreMultiplier))
                            scoreMultiplier *= 2;

                        Gameplay.instance.incScore(scorePerPkg * scoreMultiplier);

                        // Zobrazit popup
                        HUD.instance.showScorePopup(GameWorld.PATH_X + GameWorld.PATH_WIDTH / 2, 0.5, point.worldPos, scoreMultiplier + "X " + scorePerPkg);

                        // Hrac uz nenese zadne balicky                        
                        this._deliveryBonus.reset();
                        this._pkgCnt = 0;
                        this._pkgCharMaxCnt = 0;

                        // Vyvolat udalost                        
                        let gameplay = Gameplay.instance;

                        gameplay.events.fire(Player.EVENT_PARCEL_DELIVERED, pkgCnt, this._deliveredPkgCnt);
                        gameplay.events.fire(Player.EVENT_PARCEL_CNT_CHANGE, 0, 0);

                        if (gameplay.state == eState.lvlComplete)
                            gameplay.events.fire(Player.EVENT_STATE_CHANGE, (this._state = ePlayerState.idle));

                        // Audio
                        SoundManager.playSound(this._audioEntity, eSfxSprite.pkgDelivery);

                        return false;
                    }

                    return true;
                });
            }

            // Zmenit smer pohybu
            this.flipWalkDir(posShift, false);
        }

        // Testovani kolizi
        this._liveChars.forEach((char: Character) => { char.checkCollisions(); });

        // Pozice kamery
        this.updateCameraOrigin();
    }

    //-------------------------------------------------------------------------
    /**
     * Zahajit chuzi
     */
    //-------------------------------------------------------------------------
    startWalking(): void {
        this._walkStartTime = TIMER.time;
        Gameplay.instance.events.fire(Player.EVENT_STATE_CHANGE, (this._state = ePlayerState.walk));
    }

    //-------------------------------------------------------------------------
    /**
     * Zmeni smer pohybu hrace
     * @param positionShift O kolik se ma hrac posunout na ose Z
     * @param controlled True, pokud je zmena vyvolana ovladanim hrace. False, pokud narazem do neceho co obraci smer hrace.
     */
    //-------------------------------------------------------------------------
    flipWalkDir(positionShift: number = 0, controlled: boolean = true): void {
        if (this._state != ePlayerState.walk || this._liveChars.empty)
            return;

        this._dir *= -1;
        this._liveChars.forEach((char: Character) => { char.setDir(this._dir, positionShift); });

        SoundManager.playSound(this._audioEntity, controlled ? eSfxSprite.plChangeDir_0 : eSfxSprite.plChangeDir_1);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateCameraOrigin(): void {
        if (!this._liveChars.empty) {
            this._camOrigin = this._liveChars.first.position.z;
            this._camOrigin += (this._liveChars.last.position.z - this._camOrigin) / 2;

            // Pozice audio entity
            this._audioEntity.setPosition(GameWorld.PATH_X + GameWorld.PATH_WIDTH / 2, 0, this._camOrigin);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleCharPkgPickup(char: Character): void {
        if (char.packageCnt > this._pkgCharMaxCnt)
            this._pkgCharMaxCnt = char.packageCnt;

        Gameplay.instance.events.fire(Player.EVENT_PARCEL_CNT_CHANGE, ++this._pkgCnt, this._pkgCharMaxCnt);

        // Delivery bonus
        this._deliveryBonus.start(this._pkgCharMaxCnt);

        // Audio
        if (TIMER.time - this._pkgPickupSfxTime >= 100) {
            this._pkgPickupSfxTime = TIMER.time;
            SoundManager.playSound(this._audioEntity, eSfxSprite.pkgPickup)
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Zpracovat smrt postavy
     * @param char
     */
    //-------------------------------------------------------------------------
    private handleCharDeath(char: Character, deathReason: eDeathReason): void {
        // Odstranit postavu ze seznamu zivych postav
        let charRemoved = false;

        this._liveChars.forEach((c: Character, node: Node<Character>) => {
            if (c == char) {
                this._dyingChars.add(char);
                this._liveChars.removeByNode(node);
                charRemoved = true;
            }
            else {
                // Upravit offset zive postavy od okraje formace
                if (!charRemoved) {
                    if (c.formationBotOffset != 0)
                        c.formationBotOffset--;
                }
                else if (c.formationTopOffset != 0) {
                    c.formationTopOffset--;
                }
            }
        });

        // Snizit celkovy pocet balicku nesenych hracem
        if (char.packageCnt != 0) {
            this._pkgCnt -= char.packageCnt;

            if (this._pkgCharMaxCnt != 0 && char.packageCnt == this._pkgCharMaxCnt) {
                // Najit postavu s nejvetsim poctem baliku (mimo postavy, ktera zemrela)
                this._pkgCharMaxCnt = 0;

                this._liveChars.forEach((c: Character) => {
                    if (c != char && c.packageCnt > this._pkgCharMaxCnt)
                        this._pkgCharMaxCnt = c.packageCnt;
                });
            }

            // Delivery bonus
            if (this._pkgCnt == 0)
                this._deliveryBonus.reset();

            Gameplay.instance.events.fire(Player.EVENT_PARCEL_CNT_CHANGE, this._pkgCnt, this._pkgCharMaxCnt);
        }

        // Audio
        SoundManager.playSound(this._audioEntity, eSfxSprite.plDeath);
    }
}